import axios, { AxiosInstance } from "axios";
import { BASE_URL } from "configs/api";
import { notify } from "hooks/useToast/notify";
import { campaignsStore } from "./store";
import {
  ICampaign,
  ICampaignsResponse,
  ICampaignsRule,
  IProject,
  IProjectsResponse,
  ITaskForm,
  ITaskResponse
} from "./types";

class CampaignsApi {
  private _api: AxiosInstance;

  constructor() {
    this._api = axios.create({
      baseURL: BASE_URL,
      withCredentials: true
    });
  }

  private handleError(error: any) {
    if (error.response) {
      notify({
        type: "error",
        meassage: error.response.data.message
      });
    } else {
      notify({
        type: "error",
        meassage: "Something went wrong"
      });
    }
  }

  async createProject(projectData: IProject, callback?: () => void) {
    const url = `/api/projects`;
    const { setIsLoading } = campaignsStore.getState();

    setIsLoading(true);

    try {
      const payload = {
        title: projectData.title,
        xUrl: projectData.x_url,
        project_logo: projectData.project_url,
        twitterHandle: projectData.twitter_handle
          ? projectData.twitter_handle
          : "",
        campaignId: projectData.campaignId
      };

      await this._api.post(url, payload);

      if (!!callback) {
        callback();
      }

      notify({
        type: "success",
        meassage: "Project has been successfully created"
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      setIsLoading(false);
    }
  }

  async updateProject(projectData: IProject, callback?: () => void) {
    const url = `/api/projects/${projectData.id}`;
    const { setIsLoading } = campaignsStore.getState();

    setIsLoading(true);

    try {
      const payload = {
        title: projectData.title,
        xUrl: projectData.x_url,
        project_logo: projectData.project_url,
        twitterHandle: projectData.twitter_handle
          ? projectData.twitter_handle
          : "",
        campaignId: projectData.campaignId
      };

      await this._api.put(url, payload);

      if (!!callback) {
        callback();
      }

      notify({
        type: "success",
        meassage: "Project has been successfully updated"
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      setIsLoading(false);
    }
  }

  async getAllProjects(page: number, callback?: () => void) {
    const url = `/api/projects`;
    const { setHasMoreProjects, setTotalProjectsPages } =
      campaignsStore.getState();
    const limit = 10;
    try {
      const { data } = await this._api.get<IProjectsResponse>(url, {
        params: {
          page: page,
          limit,
        }
      });

      if (!!callback) {
        callback();
      } 
      
      const isThereMore = page < Math.ceil(data.total / limit);
      setTotalProjectsPages(data.total);
      setHasMoreProjects(isThereMore);

      return data.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteProject(id: string) {
    const url = `/api/projects/${id}`;
    try {
      await this._api.delete(url);
      notify({
        type: "success",
        meassage: "Project has been successfully deleted"
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async getAllCampaigns(
    page: number,
    callback?: () => void
  ): Promise<ICampaign[]> {
    const url = `/api/campaigns`;
    const {setHasMoreCampaigns, setTotalCampaignsPages} = campaignsStore.getState();
    const limit = 10;
    try {
      const { data } = await this._api.get<ICampaignsResponse>(url, {
        params: {
          page: page,
          limit,
        }
      });

      if (!!callback) {
        callback();
      }

      const isThereMore = page < Math.ceil(data.total / limit);
      setTotalCampaignsPages(data.total);
      setHasMoreCampaigns(isThereMore);

      return data.data;
    } catch (error) {
      this.handleError(error);
      return [];
    }
  }

  async createCampaign(campaignData: ICampaign, callback?: () => void) {
    const url = `/api/campaigns`;
    const { setIsCampaignIteracting, campaignRules } = campaignsStore.getState();

    setIsCampaignIteracting(true);

    try {
      const payload = {
        title: campaignData.title,
        banner_url: campaignData.banner_url,
        banner_injectable_url: campaignData.secondary_banner_url,
        isActive: campaignData.is_active,
        startDate: campaignData.start_date,
        endDate: campaignData.end_date,
        ...(!!campaignData.discord_role_id ? {
          discord_role_id: campaignData.discord_role_id
        } : {}),
        ...(!!campaignData.rewards.length ? {
          rewards: this.normalizeRewards(campaignData.rewards)
        } : {}),
      };

      const { data } = await this._api.post<ICampaign>(url, payload);

      if (campaignRules && campaignRules.length > 0 && data.id) {
        const updatedCampaignRules = campaignRules.map((rule) => ({
          ...rule,
          campaignId: data.id
        }));

        await this.createRules(updatedCampaignRules);
      }

      if (!!callback && data.id) {
        callback();
      }

      notify({
        type: "success",
        meassage: "Campaign has been successfully created"
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      setIsCampaignIteracting(false);
    }
  }

  async updateCampaign(campaign: ICampaign, cb?: () => void) {
    const { setIsCampaignIteracting } = campaignsStore.getState();
    const payload = {
      title: campaign.title,
      banner_url: campaign.banner_url,
      banner_injectable_url: campaign.secondary_banner_url,
      isActive: campaign.is_active,
      startDate: campaign.start_date,
      endDate: campaign.end_date,
      campaign_rules: campaign.campaign_rules,
      discord_role_id: campaign.discord_role_id ?? null,
      ...(!!campaign.rewards.length ? {
        rewards: this.normalizeRewards(campaign.rewards)
      } : {}),
    };

    try {
      setIsCampaignIteracting(true);
      const { data } = await this._api.put(
        `/api/campaigns/${campaign.id}`,
        payload
      );
      cb?.();

      notify({
        meassage: "Successfully updated campaign",
        type: "success"
      });

      return data;
    } catch (error) {
      this.handleError(error);
    } finally {
      setIsCampaignIteracting(false);
    }
  }

  async deleteCampaign(id: string) {
    const url = `/api/campaigns/${id}`;

    try {
      await this._api.delete(url);

      notify({
        meassage: "Successfully deleted campaign",
        type: "success"
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async createTask(taskData: ITaskForm, callback?: () => void) {
    const url = "/api/campaigns-task";
    const { setIsLoading } = campaignsStore.getState();

    setIsLoading(true);
    try {
      const payload = {
        type: taskData.type,
        title: taskData.title,
        order: Number(taskData.order),
        source: taskData.source,
        projectId: taskData.projectId,
        guildId: taskData.guildId,
        roleId: taskData.roleId,
        channelLink: taskData.channelLink,
      };
    console.log("taskData", payload);

      await this._api.post(url, payload);

      if (!!callback) {
        callback();
      }

      notify({
        type: "success",
        meassage: "Task has been successfully created"
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      setIsLoading(false);
    }
  }

  async getTasks(
    page: number,
    limit: number = 10,
    callback?: () => void
  ): Promise<ITaskResponse> {
    const url = `/api/campaigns-task`;
    try {
      const { data } = await this._api.get<ITaskResponse>(url, {
        params: {
          page: page,
          limit
        }
      });

      if (!!callback) {
        callback();
      }

      return data;
    } catch (error) {
      this.handleError(error);
      return {
        data: [],
        total: 0
      };
    }
  }

  async updateTask(taskData: ITaskForm, callback?: () => void) {
    const url = `/api/campaigns-task/${taskData.id}`;
    const { setIsLoading } = campaignsStore.getState();

    try {
      setIsLoading(true);
      const payload = {
        type: taskData.type,
        title: taskData.title,
        order: Number(taskData.order),
        source: taskData.source,
        projectId: taskData.projectId,
        guildId: taskData.guildId,
        roleId: taskData.roleId,
        channelLink: taskData.channelLink,
      };

      const { data } = await this._api.put(url, payload);
      callback?.();

      notify({
        meassage: "Successfully updated task",
        type: "success"
      });

      return data;
    } catch (error) {
      this.handleError(error);
    } finally {
      setIsLoading(false);
    }
  }

  async deleteTask(id: string) {
    const url = `/api/campaigns-task/${id}`;

    try {
      await this._api.delete(url);

      notify({
        meassage: "Successfully deleted task",
        type: "success"
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async createRules(
    rules: ICampaignsRule[]
  ): Promise<ICampaignsRule | undefined> {
    const url = "/api/campaign-rules/bulk";
    const reqBody = {
      rules
    };

    try {
      const { data } = await this._api.post<ICampaignsRule>(url, reqBody);
      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createRule(
    rule: ICampaignsRule,
    campaignId: string
  ): Promise<ICampaignsRule | undefined> {
    const url = "/api/campaign-rules";
    const reqBody = {
      ...rule,
      campaignId
    };

    try {
      const { data } = await this._api.post<ICampaignsRule>(url, reqBody);

      notify({
        meassage: "Successfully created rule",
        type: "success"
      });

      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateRule(rule: ICampaignsRule, campaignId: string) {
    const url = `/api/campaign-rules/${campaignId}`;

    try {
      const { data } = await this._api.put(url, rule);

      notify({
        meassage: "Successfully updated rule",
        type: "success"
      });

      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteRule(ruleId: string) {
    const url = `/api/campaign-rules/${ruleId}`;

    try {
      await this._api.delete(url);

      notify({
        meassage: "Successfully deleted rule",
        type: "success"
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  normalizeRewards(rewards: string[]) {
    return rewards
      .map((reward) => reward.trim())
      .filter((reward) => !!reward);
  }
}

export const campaignsApi = new CampaignsApi();
